import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { api } from '../api/client'
import type { AccessFonte } from '../api/types'
import { Card, EmptyState, ErrorBanner, Spinner, StatCard, formatDateTime, timeAgo } from '../components/common'
import { DataTable } from '../components/DataTable'
import { GraficoOre, etichettaFonte } from '../components/access/comuni'
import { VistaEventi } from '../components/access/Eventi'
import { VistaImpostazioni } from '../components/access/Impostazioni'
import { VistaPersone } from '../components/access/Persone'
import { VistaPrivilegi } from '../components/access/Privilegi'
import { VistaRilevamenti } from '../components/access/Rilevamenti'
import { VistaSyslog } from '../components/access/Syslog'
import { VistaUtenze } from '../components/access/Utenze'
import { EsitoVerifica, VistaRegistro } from '../components/access/Registro'
import { useStatoAccess } from '../hooks/useAccess'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'
import { VistaPostura } from '../components/access/Postura'

/**
 * CENSO ACCESS (ADR-0030): the register of the accesses, a segregated area.
 *
 * Who entered which system, when, from where and with which privileges —
 * administrators always, users only on the tenant's declaration — tied to the
 * CMDB (the system is a CI, the person one identity across domain, Linux,
 * Entra ID and devices). The views follow the menu; the person's page is
 * `AccessPersonPage`. Only the area's roles open it: not the tenant's
 * administrator, not the platform's.
 */

function Panoramica() {
  const dati = useAsync(() => api.accessOverview(), [])
  const o = dati.data
  if (dati.loading && !o) return <Spinner />
  if (!o) return <ErrorBanner error={dati.error} />
  return (
    <>
      <div className="stats">
        <StatCard label={t('Accessi privilegiati (24 ore)')} value={o.privileged_24h} to="/access/events?periodo=24h&privilegiati=1&esito=success" />
        <StatCard label={t('Privilegiati fuori orario (24 ore)')} value={o.off_hours_privileged_24h} tone={o.off_hours_privileged_24h > 0 ? 'warn' : undefined}
                  to="/access/events?periodo=24h&privilegiati=1&fuori=1&esito=success" />
        <StatCard label={t('Accessi falliti (24 ore)')} value={o.failures_24h} tone={o.failures_24h > 0 ? 'warn' : undefined} to="/access/events?periodo=24h&esito=failure" />
        <StatCard label={t('Rilevamenti aperti')} value={o.detections_open} tone={o.detections_open > 0 ? 'error' : 'ok'} to="/access/detections" />
        <StatCard label={t('Persone con privilegi')} value={o.privileged_identities} hint={t('su {n} persone nel registro', { n: o.identities })} to="/access/people" />
        <StatCard label={t('Fonti silenziose')} value={`${o.sources_silent}/${o.sources}`} tone={o.sources_silent > 0 ? 'warn' : undefined} to="/access/sources" />
      </div>
      <Card title={t('Ultime 24 ore')}>
        <GraficoOre punti={o.by_hour} />
        {Object.keys(o.by_source).length > 0 && (
          <p className="field__hint">{Object.entries(o.by_source).map(([f, n]) => `${etichettaFonte(f)}: ${n}`).join(' · ')}</p>
        )}
      </Card>
      <Card title={t('Il registro')}>
        <dl className="kv kv--attributi">
          <dt>{t('Eventi conservati')}</dt><dd>{o.total_events.toLocaleString()} <span className="muted">· {t('conservazione {n} giorni', { n: o.retention_days })}</span></dd>
          <dt>{t('Integrità')}</dt><dd><EsitoVerifica v={o.last_verification} />{o.last_verification?.at && <span className="muted"> · {formatDateTime(o.last_verification.at)}</span>} · <Link to="/access/ledger">{t('verifica')}</Link></dd>
          <dt>{t('Utenti non privilegiati')}</dt><dd>{o.standard_users ? t('registrati, sulla base giuridica dichiarata') : t('non registrati: solo i tentativi falliti, senza il nome')} · <Link to="/access/settings">{t('impostazioni')}</Link></dd>
        </dl>
      </Card>
    </>
  )
}

function Fonti({ scrive }: { scrive: boolean }) {
  const dati = useAsync(() => api.accessSources(), [])
  const righe = dati.data?.sources ?? []
  const [errore, setErrore] = useState<unknown>(null)
  const credenziali = useAsync(() => (scrive ? api.accessEntraCredentials() : Promise.resolve({ credentials: [] })), [scrive])
  const [scelta, setScelta] = useState('')
  async function aggiungiEntra() {
    setErrore(null)
    try {
      await api.accessEntraAdd(scelta)
      setScelta('')
      void credenziali.reload()
      void dati.reload()
    } catch (err) {
      setErrore(err)
    }
  }
  async function cambia(s: AccessFonte) {
    setErrore(null)
    try {
      await api.accessSourceSet(s.id, !s.enabled)
      dati.reload()
    } catch (err) {
      setErrore(err)
    }
  }
  return (
    <Card title={t('Fonti del registro')}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Ogni agente, domain controller, sonda e credenziale di Entra ID che consegna eventi compare qui, con l’ultima consegna e gli eventi portati. Una fonte silenziosa da più di due ore è segnalata nella panoramica: un registro con un buco non dimostra niente per quel periodo.')}
      </p>
      {scrive && (
        <div className="accfiltri">
          <span className="muted">{t('Entra ID e Azure:')}</span>
          {(credenziali.data?.credentials ?? []).filter((c) => !c.in_use).length === 0 ? (
            <span className="muted">{(credenziali.data?.credentials ?? []).length === 0
              ? t('serve una credenziale Microsoft Azure (Credenziali), con i permessi descritti nella sua guida')
              : t('tutte le credenziali Azure sono già fonti')}</span>
          ) : (
            <>
              <select value={scelta} onChange={(e) => setScelta(e.target.value)}>
                <option value="">{t('scegli la credenziale Azure')}</option>
                {(credenziali.data?.credentials ?? []).filter((c) => !c.in_use).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <button type="button" onClick={() => void aggiungiEntra()} disabled={!scelta}>{t('Aggiungi Entra ID')}</button>
            </>
          )}
        </div>
      )}
      <ErrorBanner error={dati.error ?? errore} />
      {dati.loading && !dati.data ? <Spinner /> : righe.length === 0 ? (
        <EmptyState title={t('Nessuna fonte ha ancora consegnato eventi.')} hint={t('Le fonti si accendono con gli agenti aggiornati (Windows, Linux, domain controller), con la funzione syslog di una sonda per apparati di rete e Wi‑Fi, e con una credenziale Azure per Entra ID.')} />
      ) : (
        <DataTable rows={righe} rowKey={(s) => s.id} conteggio={{ nome: t('fonti') }} columns={[
          { key: 'name', header: t('Fonte'), value: (s) => s.name, render: (s) => <strong>{s.name}</strong> },
          { key: 'kind', header: t('Tipo'), filter: 'select', value: (s) => ({ agent: t('agente'), probe: t('sonda (syslog)'), entra: t('Entra ID'), inventory: t('inventario') } as Record<string, string>)[s.kind] ?? s.kind },
          { key: 'method', header: t('Lettura'), value: (s) => metodoFonte(s), render: (s) => <span title={statoFonte(s)}>{metodoFonte(s)}</span> },
          { key: 'enabled', header: t('Stato'), filter: 'select', value: (s) => s.enabled ? t('attiva') : t('sospesa'),
            render: (s) => scrive && s.kind !== 'inventory'
              ? <button type="button" className="link" onClick={() => void cambia(s)}>{s.enabled ? t('attiva · sospendi') : t('sospesa · riprendi')}</button>
              : (s.enabled ? t('attiva') : t('sospesa')) },
          { key: 'last_delivery_at', header: t('Ultima consegna'), type: 'date', value: (s) => s.last_delivery_at ?? '', render: (s) => s.last_delivery_at ? <span title={formatDateTime(s.last_delivery_at)}>{timeAgo(s.last_delivery_at)}</span> : '—' },
          { key: 'last_event_at', header: t('Ultimo evento'), type: 'date', value: (s) => s.last_event_at ?? '', render: (s) => s.last_event_at ? formatDateTime(s.last_event_at) : '—' },
          { key: 'events_total', header: t('Eventi consegnati'), type: 'number', value: (s) => s.events_total },
          { key: 'agent_version', header: t('Agente'), value: (s) => s.kind === 'agent' ? (s.agent_version ?? '') : '',
            render: (s) => s.kind !== 'agent' ? <span className="muted">—</span>
              : s.agent_missing ? <span className="muted">{t('non più presente')}</span>
                : <span title={s.agent_seen_at ? t('ultimo contatto {quando}', { quando: formatDateTime(s.agent_seen_at) }) : undefined}>{s.agent_version ?? '—'}</span> },
          { key: 'last_error', header: t('Problema'), value: (s) => [s.superseded ? t('installazione sostituita') : '', s.last_error ?? ''].filter(Boolean).join(' · '),
            render: (s) => <ProblemaFonte s={s} /> },
        ]} />
      )}
    </Card>
  )
}

const METODI: Record<string, () => string> = {
  security_log: () => t('registro Security'),
  'security_log+dc': () => t('registro Security (domain controller)'),
  journald: () => t('journald'),
  graph: () => t('Microsoft Graph'),
  'graph+arm': () => t('Microsoft Graph e activity log di Azure'),
  none: () => t('nessuna'),
}

/** The problem of a source, explained: a replaced installation, a query the
 *  Security log refuses (an agent to update), a log read in more rounds. */
function ProblemaFonte({ s }: { s: AccessFonte }) {
  const note: string[] = []
  if (s.superseded) note.push(t('Installazione sostituita: un’altra fonte con lo stesso nome consegna, questa è ferma. Se l’agente è stato reinstallato, sospendila.'))
  if (s.last_error?.includes('query is invalid')) note.push(t('Aggiorna l’agente: le versioni nuove ripiegano da sole su una query più semplice quando il domain controller rifiuta quella di sempre.'))
  if (s.last_error?.includes('timeout period expired')) note.push(t('Registro Security grande: con l’agente aggiornato gli eventi si consegnano in più giri, senza errore.'))
  if (!s.last_error && note.length === 0) return <span className="muted">—</span>
  return (
    <>
      {s.last_error && <small className="eagle-motivo">{s.last_error}</small>}
      {note.map((n) => <small key={n} className="muted" style={{ display: 'block' }}>{n}</small>)}
    </>
  )
}

function metodoFonte(s: AccessFonte): string {
  const m = String(s.state?.method ?? '')
  if (!m) return '—'
  if (m.startsWith('file:')) return m.slice(5)
  return METODI[m]?.() ?? m
}

function statoFonte(s: AccessFonte): string {
  const st = s.state ?? {}
  const parti = []
  if (typeof st.queued === 'number') parti.push(t('in coda sull’agente: {n}', { n: st.queued }))
  if (typeof st.dropped === 'number' && st.dropped > 0) parti.push(t('scartati per coda piena: {n}', { n: st.dropped }))
  return parti.join(' · ')
}

function InArrivo({ titolo, testo }: { titolo: string; testo: string }) {
  return <Card title={titolo}><EmptyState title={t('In preparazione.')} hint={testo} /></Card>
}

export default function AccessArea() {
  const { vista } = useParams()
  const stato = useStatoAccess()
  const viste: Record<string, { titolo: string; sotto: string; corpo: JSX.Element }> = {
    events: { titolo: t('Accessi'), sotto: t('Il registro: chi è entrato in quale sistema, quando, da dove, con quali privilegi.'), corpo: <VistaEventi /> },
    people: { titolo: t('Persone'), sotto: t('Le persone dietro gli account: una riga per persona, unita fra dominio, account locali, Linux, Entra ID e apparati.'), corpo: <VistaPersone /> },
    posture: { titolo: t('Postura delle utenze'), sotto: t('Quanto sono in ordine le utenze: zombie, mai usate, password che non scadono o vecchie, privilegiate a rischio, e come migliorare.'), corpo: <VistaPostura /> },
    privileges: { titolo: t('Privilegi'), sotto: t('Chi è amministratore di che cosa, da quando, e le variazioni.'), corpo: <VistaPrivilegi /> },
    accounts: { titolo: t('Gestione utenze'), sotto: t('Chi crea, modifica, abilita, disabilita e cancella le utenze, chi cambia le password e i gruppi.'), corpo: <VistaUtenze /> },
    detections: { titolo: t('Rilevamenti'), sotto: t('Ciò che le correlazioni hanno trovato: fuori orario, forza bruta, movimento laterale, nuovi privilegi.'), corpo: <VistaRilevamenti scrive={Boolean(stato.scrive)} /> },
    reports: { titolo: t('Report'), sotto: t('I report per il Garante, NIS2, DORA, PCI DSS, ISO 27001.'), corpo: <InArrivo titolo={t('Report')} testo={t('I report per quadro normativo arrivano con una delle prossime consegne di CENSO ACCESS.')} /> },
    sources: { titolo: t('Fonti'), sotto: t('Da dove arrivano gli eventi e come stanno le fonti.'), corpo: <><Fonti scrive={Boolean(stato.scrive)} /><VistaSyslog /></> },
    settings: { titolo: t('Impostazioni'), sotto: t('Orario di lavoro, conservazione, utenti non privilegiati, gruppi privilegiati, soglie dei rilevamenti.'), corpo: <VistaImpostazioni scrive={Boolean(stato.scrive)} /> },
    ledger: { titolo: t('Registro dell’area'), sotto: t('L’integrità della catena, chi ha accesso all’area e chi ha consultato il registro.'), corpo: <VistaRegistro scrive={Boolean(stato.scrive)} /> },
  }
  const scelta = vista ? viste[vista] : undefined
  return (
    <>
      <header className="page__header">
        <div>
          <h1>{scelta ? scelta.titolo : t('CENSO ACCESS')}</h1>
          <p className="page__subtitle">{scelta ? scelta.sotto : t('Il registro degli accessi di amministratori e utenti, legato all’inventario: inalterabile, conservato, consultabile solo da chi ne ha il ruolo.')}</p>
        </div>
      </header>
      {scelta ? scelta.corpo : <Panoramica />}
    </>
  )
}
